import { isNullOrUndefined, isNullOrUndefinedOrEmpty, utc, UTC, guid, debounce } from 'rewire-common';
import { freeze } from 'rewire-core';
import { Form, IFormContext } from 'rewire-ui';
import { DeepPartial, FieldFilter, ITemplate, TemplateFilter, TemplateSort } from '../DataTypes';
import DataMutations from '../graphql/Mutations';
import { localTimeZoneUTCNow, utcDateToLocalDate } from '../utility/DateUtils';
import { notifications } from '../ui/Notifications';
import DocumentDialogModel, { DocumentSchema } from './DocumentDialogModel';
import { IEditDialogModelState } from './EditDialogModel';
import GeneratorCalendarModel, { ITemplateCalendarEvent, IGeneratorCalendarOptions } from './GeneratorCalendarModel';
import mainPageModel from '../main-navigation/MainPageModel';
import templateDialogModel from './TemplateDialogModel';
import DataQueries from '../graphql/Queries';

const GeneratorSchema = `
  ${DocumentSchema}
  locked
  displayStartDate
  cycle {
    id
    code
    fields
    locked
    template {
      start
      end
      units
      reason {
        name
      }
    }
  }
`;

interface IGeneratorDialogModelState extends IEditDialogModelState {
  locked: boolean;
  draggedEvent?: ITemplateCalendarEvent;
  templateCalendarEvents: ITemplateCalendarEvent[];
}

export class GeneratorDialogModel extends DocumentDialogModel<IGeneratorDialogModelState> {
  generatorCalendarModel: GeneratorCalendarModel;
  private _active: boolean;

  constructor() {
    super(
      'Shift Pattern',
      (ids: string[]) => DataQueries.getGeneratorsByIds(GeneratorSchema, ids),
      DataMutations.insertGenerators,
      DataMutations.updateGenerators,
      DataMutations.deleteGenerators,
      'generators',
      () => mainPageModel.canAddGenerators,
      () => mainPageModel.canEditGenerators,
      () => mainPageModel.canDeleteGenerators,
    );
    // this.state = super.state as any;
    this.generatorCalendarModel = new GeneratorCalendarModel();
    this.state.templateCalendarEvents = [];
    this.state.locked = false;
    this._active = false;
  }

  protected _createForm(): Form {
    this.handleTemplateCodeSearchChange = debounce(this.handleTemplateCodeSearchChange, 150);
    const form = super._createForm();
    form.fields.forEach((f) => {
      if ((this.viewOnly || (f.name !== 'name' && f.name !== 'code')) && f.name !== 'templateCodeSearch') {
        const disabled = f.disabled;
        f.disabled = () => !!((f.name !== 'name' && f.name !== 'code' && this.locked) || disabled?.(f));
      }
      f.variant = 'outlined';
    });

    return form;
  }

  protected otherFields(form: IFormContext) {
    return {
      repeatEveryXDays: form
        .number({ allowNegative: false, format: '###' })
        .label('X # of Days')
        .validators('required')
        .updateOnChange()
        .onValueChange((form, v) => this.handleRepeatEveryXDaysChange(form, v))
        .disabled(() => this.locked),
      calendarStartDate: form
        .date()
        .label('Display on calendar as of')
        .validators('required')
        .updateOnChange()
        .onValueChange((form, v) => this.handleCalendarStartDateChange(form, v))
        .disabled(() => this.locked),
      templateCodeSearch: form
        .string()
        .label('Shift Type Code Search')
        .updateOnChange()
        .onValueChange((form, v) => this.handleTemplateCodeSearchChange(form, v)),
    };
  }

  protected get viewOnlyAccessExceptionsFieldNames() {
    return ['templateCodeSearch'];
  }

  activate() {
    if (!this._active) {
      this._active = true;
      templateDialogModel.onEvent.addListener('submit', this.handleTemplateDialogOnSubmit);
    }
  }

  deactivate() {
    if (this._active) {
      this._active = false;
      templateDialogModel.onEvent.removeListener('submit', this.handleTemplateDialogOnSubmit);
    }
  }

  reset() {
    this._reset();
    this.state.templateCalendarEvents = [] as ITemplateCalendarEvent[];
    this.isLoading = false;
  }

  _reset() {
    super._reset({ repeatEveryXDays: 7, calendarStartDate: localTimeZoneUTCNow().startOfDay() });
    freeze(() => {
      this.state.locked = false;
      this.state.draggedEvent = undefined;
    });
    this.generatorCalendarModel.reset();
  }

  async load(id?: string, isModal: boolean = true) {
    this.activate();
    this.isLoading = true;
    this.id = id;
    this.isModal = isModal;
    this.state.draggedEvent = undefined;
    await this.updateTemplates();
    let generatorCalendarData: ITemplateCalendarEvent[] | undefined;
    if (!this.isNew) {
      const results = await this._load([this.id!]);
      if (!results?.length || !results[0]) {
        this._reset();
      } else {
        const generator = results[0];
        generatorCalendarData = this.generatorCalendarModel.getTemplateCalendarEvents(generator.cycle);
        this.state.locked = !!generator.locked;
        this.form.value = this.mapToForm(results[0]);
      }
    } else {
      this._reset();
    }

    this.state.title = this.getTitle();
    const generatorCalendarOptions: IGeneratorCalendarOptions = {
      calendarStartDate: this.calendarStartDate,
      calendarEndDate: this.calendarEndDate,
      calendarDisplayDate: this.calendarStartDate,
      templateCalendarData: generatorCalendarData || this.getDefaultGeneratorCalendarData(),
      locked: this.viewOnly || this.locked,
    };

    this.generatorCalendarModel.load(generatorCalendarOptions);
    this.open();
    this.isLoading = false;
  }

  async updateTemplates() {
    const templates = await this.getTemplatesData();
    this.state.templateCalendarEvents = this.generatorCalendarModel.getTemplateCalendarEvents(templates as ITemplate[]);
  }

  public mapToForm(obj: ObjectType): ObjectType {
    const formData = {
      ...obj,
      repeatEveryXDays: obj.cycle ? obj.cycle.length : 7,
      calendarStartDate: obj.displayStartDate || localTimeZoneUTCNow().startOfDay(),
    };
    return formData;
  }

  getTemplatesData = async (): Promise<DeepPartial<ITemplate>[]> => {
    const returnSchema = `
      id
      name
      code
      fields
      locked
      template {
        start
        end
        units
        reason {
          name
        }
      }
    `;
    const searchFilter = this.form.field.templateCodeSearch.value;
    const templateFilter: TemplateFilter = {};
    if (!isNullOrUndefinedOrEmpty(searchFilter)) {
      templateFilter['custom'] = [{ field: 'code', match: searchFilter }];
    }
    const templateSort: TemplateSort = { name: { direction: 'asc' } };

    const result = await DataQueries.getTemplates(returnSchema, templateFilter, templateSort, 200);
    return result && result.data;
  };

  getDefaultGeneratorCalendarData(): ITemplateCalendarEvent[] {
    const cycle = new Array(this.repeatEveryXDays);
    return this.generatorCalendarModel.getTemplateCalendarEvents(cycle);
  }

  get locked(): boolean {
    return this.state.locked;
  }

  get draggedEvent(): ITemplateCalendarEvent | undefined {
    return this.state.draggedEvent;
  }

  get isDragging(): boolean {
    return !!this.draggedEvent;
  }

  get templateCalendarEvents(): ITemplateCalendarEvent[] {
    return this.state.templateCalendarEvents;
  }

  get repeatEveryXDays(): number | undefined {
    return this.form.field.repeatEveryXDays.value;
  }

  get calendarStartDate(): UTC {
    return this.form.field.calendarStartDate.value || localTimeZoneUTCNow().startOfDay();
  }

  get calendarEndDate(): UTC | undefined {
    if (isNullOrUndefined(this.repeatEveryXDays)) {
      return undefined;
    }

    return this.calendarStartDate.add(this.repeatEveryXDays! - 1);
  }

  protected hasChanges(): boolean {
    return (
      this.form.field.name.value !== this.form.value.name ||
      (!this.locked && (this.form.hasChanges || this.generatorCalendarModel.changed))
    );
  }

  setDraggedEvent(event: ITemplateCalendarEvent) {
    this.state.draggedEvent = event;
  }

  onDropFromOutside = ({ start }: { start: Date; end: Date; allDay: boolean }) => {
    if (this.draggedEvent) {
      const utcVal = utc(utcDateToLocalDate(start));
      this.generatorCalendarModel.updateEvents(this.draggedEvent, utcVal, utcVal.clone());
      this.state.draggedEvent = undefined;
    }
  };

  canDropFromOutside = (): boolean => {
    return true;
  };

  handleCalendarStartDateChange = (_form: Form, _v?: UTC) => {
    this.generatorCalendarModel.setCalendarStartDate(this.calendarStartDate);
    this.generatorCalendarModel.setCalendarEndDate(this.calendarEndDate);
    this.generatorCalendarModel.setCalendarDisplayDate(this.calendarStartDate);
  };

  handleRepeatEveryXDaysChange = (form: Form, v?: number) => {
    if (!isNullOrUndefined(v)) {
      freeze(() => {
        this.generatorCalendarModel.setCalendarEndDate(this.calendarEndDate);
        const numOfEvents = this.generatorCalendarModel.templateCalendarEvents.length;
        for (let i = v!, j = numOfEvents; i > numOfEvents; i--, j++) {
          const calendarEventDate = this.calendarStartDate.add(j);
          this.generatorCalendarModel.templateCalendarEvents.push({
            id: guid(),
            templateId: undefined,
            title: undefined,
            start: calendarEventDate,
            end: calendarEventDate.clone(),
            allDay: true,
            color: undefined,
          });
        }
      });
    }
  };

  handleTemplateCodeSearchChange = async (_form: Form, _v?: UTC) => {
    await this.updateTemplates();
  };

  handleTemplateDialogOnSubmit = async () => {
    await this.updateTemplates();
  };

  showTemplateDialog(templateId?: string) {
    templateDialogModel.load(templateId);
  }

  showDuplicateGeneratorDialog() {
    if (!this.id || this.hasChanges()) {
      return;
    }
    // load dialog
  }

  deleteGenerator = async () => {
    if (!this.canDelete || this.locked) {
      return;
    }
    await this.remove();
  };

  showDuplicateTemplateDialog(_templateId: string) {
    // load dialog
  }

  deleteTemplate = async (templateId: string) => {
    const result = await DataMutations.deleteTemplates([templateId]);
    if (result) {
      notifications.success('Template Deleted');
    } else {
      notifications.error('Template Deletion Failed');
    }
  };

  public mapToSave(): ObjectType {
    const generatorInput = super.mapToSave();
    const templateIds: (string | undefined)[] = [];

    const generatorData = this.generatorCalendarModel.validTemplateCalendarEvents; // get templates from calendar
    generatorData.forEach((templateCalendarEvent: ITemplateCalendarEvent) => {
      templateIds.push(templateCalendarEvent.templateId);
    });

    // TODO can delete fields may not exist??
    delete generatorInput.repeatEveryXDays;
    delete generatorInput.calendarStartDate;
    delete generatorInput.templateCodeSearch;
    generatorInput.cycle = templateIds;
    generatorInput.displayStartDate = this.calendarStartDate;

    return generatorInput;
  }

  protected async save(): Promise<boolean> {
    // check to see if a shift type with that name already exists, or should we be just attempting to submit, and letting it fail?
    if (this.isNew) {
      const exists = await this.hasExistingGeneratorWithSameName(this.form.field.name.value);
      if (exists) {
        notifications.error('Shift Pattern Creation Failed: Shift Pattern with that name already exists');
        return false;
      }
    }

    return await super.save();
  }

  protected async hasExistingGeneratorWithSameName(name: string): Promise<boolean> {
    const generatorFilter: FieldFilter = {
      field: 'name',
      eq: name,
    };

    const generatorSearch = await DataQueries.getSearch('', 'generators', '', generatorFilter);
    return !!generatorSearch?.length;
  }
}

const generatorDialogModel = new GeneratorDialogModel();
export default generatorDialogModel;
